import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { getAffiliations } from "@/api/cfoAffiliations";
import type { CfoAffiliationRead, EmbeddedContact } from "@/api/types";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const key = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase().replace(/\/+$/, "");

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** The company's confirmed founders, shown with its contacts (owner, 2026-09-28: "affiche
 *  les fondateurs dans les contacts"). Display only: nothing is created. A founder becomes
 *  a contact only through the recipient step, once the company qualifies and a verified
 *  professional email is found. A founder already a contact (same LinkedIn or name) is not
 *  repeated. Nothing is shown to whoever may not read founder data. */
export function CfoFounderContacts({
  slug,
  companyId,
  contacts,
}: {
  slug: string;
  companyId: number;
  contacts: EmbeddedContact[];
}) {
  const [founders, setFounders] = useState<CfoAffiliationRead[]>([]);

  useEffect(() => {
    let live = true;
    getAffiliations(slug, companyId)
      .then((list) => {
        if (live) setFounders(list.items);
      })
      .catch(() => {
        if (live) setFounders([]);
      });
    return () => {
      live = false;
    };
  }, [slug, companyId]);

  const known = new Set(
    contacts.flatMap((c) => [key(c.linkedin_url), key(c.full_name)]).filter(Boolean),
  );
  // The same founder recorded by two sources, not merged yet, shows once: the fuller
  // record (its roles, its LinkedIn) is kept.
  const byPerson = new Map<string, CfoAffiliationRead>();
  for (const f of founders) {
    if (f.status !== "CONFIRMED" || f.suppressed) continue;
    if (known.has(key(f.linkedin)) || known.has(key(f.name))) continue;
    const person = key(f.name) || key(f.linkedin) || f.id;
    const kept = byPerson.get(person);
    const fuller =
      !kept ||
      f.current_roles.length > kept.current_roles.length ||
      (f.current_roles.length === kept.current_roles.length && !!f.linkedin && !kept.linkedin);
    if (fuller) byPerson.set(person, f);
  }
  const shown = [...byPerson.values()];
  if (shown.length === 0) return null;

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">
        Founders · not contacts yet
      </p>
      <ul className="space-y-2">
        {shown.map((founder) => {
          const name = founder.name ?? "Unnamed founder";
          return (
            <li key={founder.id} className="flex items-start gap-3 rounded-lg px-3 py-2">
              <Avatar className="h-9 w-9 border border-slate-200">
                <AvatarFallback className="bg-slate-100 text-xs font-semibold text-slate-600">
                  {initials(name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium text-slate-900">{name}</span>
                  <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                    Founder
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                    Not a contact yet
                  </span>
                </div>
                {founder.current_roles.length > 0 && (
                  <div className="text-xs text-slate-500">{founder.current_roles.join(" · ")}</div>
                )}
                {founder.linkedin && (
                  <a
                    href={founder.linkedin}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 pt-1 text-xs text-slate-500 hover:text-indigo-600"
                  >
                    <ExternalLink className="h-3 w-3" />
                    LinkedIn
                  </a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="mt-1 px-3 text-[11px] text-slate-400">
        A founder becomes a contact once the company qualifies and a verified professional
        email is found.
      </p>
    </div>
  );
}
